import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useContext, useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { APIContext } from 'context/APIContext'
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { GetPrimaryColorHex } from 'redux/slices/brand'
import moment from "moment"

import ScreenFrame from 'components/Frame/ScreenFrame'
import KinesisProfileBreadCrumbs from 'components/Profile/KinesisProfileBreadCrumbs'
import BrandColorButton from 'components/Buttons/BrandColorButton'
import ContentFrame from 'components/Frame/ContentFrame'

const WEEKDAYS = [
    { ISOWeekdayIndex: 0, label: 'Monday' },
    { ISOWeekdayIndex: 1, label: 'Tuesday' },
    { ISOWeekdayIndex: 2, label: 'Wednesday' },
    { ISOWeekdayIndex: 3, label: 'Thursday' },
    { ISOWeekdayIndex: 4, label: 'Friday' },
    { ISOWeekdayIndex: 5, label: 'Saturday' },
    { ISOWeekdayIndex: 6, label: 'Sunday' },
]

export default function ScheduleView({ propProfileType = 'individual' }) {

    const { urlGroupId, urlAthleteId } = useParams()

    const { GetWorkoutConfigWeekdayAssignments, SaveWorkoutConfigWeekdayAssignments } = useContext(APIContext)
    const navigate = useNavigate()
    const location = useLocation()

    const styleConfig = useSelector((state) => state.brand.styleConfig)
    const simulationClockDayOffset = useSelector((state) => state.simulationClock.simulationClockDayOffset)
    const clientDate = moment().local().add(simulationClockDayOffset, 'days').format('YYYY-MM-DD')

    const [isLoading, setIsLoading] = useState(true)
    const [schedule, setSchedule] = useState(null)
    const [workoutConfigWeekdayAssignments, setWorkoutConfigWeekdayAssignments] = useState([])
    const [formErrorMessage, setFormErrorMessage] = useState('')
    const [isSaveChangesLoading, setIsSaveChangesLoading] = useState(false)

    const workoutConfigs = useMemo(() => {
        return [...(schedule?.workoutConfigs ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    }, [schedule])

    const workoutConfigsById = useMemo(() => {
        return new Map(workoutConfigs.map((workoutConfig) => [workoutConfig.workoutConfigId, workoutConfig]))
    }, [workoutConfigs])

    const LoadSchedule = async () => {

        setIsLoading(true)

        const newSchedule = await GetWorkoutConfigWeekdayAssignments(urlGroupId, urlAthleteId, clientDate)

        setSchedule(newSchedule)
        setWorkoutConfigWeekdayAssignments([...(newSchedule?.workoutConfigWeekdayAssignments ?? [])])
        setIsLoading(false)

    }

    const GetAssignmentCountForConfig = (workoutConfigId) => {
        return workoutConfigWeekdayAssignments.filter((assignment) => assignment.workoutConfigId === workoutConfigId).length
    }

    const GetAssignableWorkoutConfigs = () => {
        return workoutConfigs.filter((workoutConfig) => {
            return GetAssignmentCountForConfig(workoutConfig.workoutConfigId) < (workoutConfig.maximumAssignmentsPerWeek ?? 0)
        })
    }

    const GetAssignmentsForDay = (ISOWeekdayIndex) => {
        return workoutConfigWeekdayAssignments
            .filter((assignment) => assignment.ISOWeekdayIndex === ISOWeekdayIndex)
            .sort((a, b) => {
                const nameA = workoutConfigsById.get(a.workoutConfigId)?.name ?? ''
                const nameB = workoutConfigsById.get(b.workoutConfigId)?.name ?? ''
                return nameA.localeCompare(nameB)
            })
    }

    const AddAssignment = (ISOWeekdayIndex, workoutConfigId) => {
        const workoutConfig = workoutConfigsById.get(workoutConfigId)

        if (!workoutConfig) return

        if (GetAssignmentCountForConfig(workoutConfigId) >= (workoutConfig.maximumAssignmentsPerWeek ?? 0)) return

        setFormErrorMessage('')

        setWorkoutConfigWeekdayAssignments((prev) => [
            ...prev,
            {
                programId: workoutConfig.programId,
                workoutConfigId: workoutConfig.workoutConfigId,
                ISOWeekdayIndex: ISOWeekdayIndex,
            },
        ])
    }

    const RemoveAssignment = (assignmentToRemove) => {
        setFormErrorMessage('')
        setWorkoutConfigWeekdayAssignments((prev) => prev.filter((assignment) => assignment !== assignmentToRemove))
    }

    const OnSaveChangesButton = async () => {

        // Every workout config must be scheduled up to its weekly maximum

        // TEMPORARILY REMOVING SCHEDULE RESTRICTIONS!
        /*
        const unscheduledWorkoutConfigs = workoutConfigs.filter((workoutConfig) => {
            return GetAssignmentCountForConfig(workoutConfig.workoutConfigId) < (workoutConfig.maximumAssignmentsPerWeek ?? 0)
        })

        if (unscheduledWorkoutConfigs.length > 0) {
            const unscheduledText = unscheduledWorkoutConfigs.map((workoutConfig) => {
                return `${workoutConfig.name} (${GetAssignmentCountForConfig(workoutConfig.workoutConfigId)}/${workoutConfig.maximumAssignmentsPerWeek ?? 0})`
            }).join(', ')

            setFormErrorMessage(`Please schedule all workouts before saving: ${unscheduledText}`)
            return
        }
        */

        setFormErrorMessage('')
        setIsSaveChangesLoading(true)

        const savedSchedule = await SaveWorkoutConfigWeekdayAssignments(schedule.programId, workoutConfigWeekdayAssignments, clientDate)

        setIsSaveChangesLoading(false)

        if (savedSchedule === false) {
            setFormErrorMessage('There was an error saving the schedule. Please try again.')
            return
        }

        // Reload so the screen reflects what was saved
        await LoadSchedule()
    }

    const OnGoToProfileButton = () => {
        if (propProfileType === 'group') {
            navigate(`/group/${urlGroupId}`)
        }

        if (propProfileType === 'individual') {
            navigate(`/athlete/${urlAthleteId}`)
        }
    }

    useEffect(() => { LoadSchedule() }, [propProfileType, urlGroupId, urlAthleteId, clientDate])

    if (isLoading) {
        return (
            <ScreenFrame>
                <div className='max-w-2xl mx-auto space-y-4'>
                    <div className='flex-shrink-0 h-20 bg-gray-200 rounded-lg animate-pulse' />
                    <div className='flex-shrink-0 h-[400px] bg-gray-200 rounded-lg animate-pulse' />
                </div>
            </ScreenFrame>
        )
    }

    if (schedule === null) {
        return (
            <ScreenFrame>
                <div className='max-w-2xl mx-auto space-y-4'>
                    <KinesisProfileBreadCrumbs 
                        propProfileType={propProfileType} 
                        profileName={location.state?.profileName} 
                        currentPageName='Schedule' 
                    />

                    <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
                        Schedule
                    </h1>

                    <ContentFrame>
                        <div className="p-10 space-y-2 text-center">
                            <p className="text-gray-600">There was an error loading the schedule.</p>
                            <p className="text-sm text-gray-500">If issues persist please contact our support team at support@kinesisintegrated.com</p>
                        </div>
                    </ContentFrame>
                </div>
            </ScreenFrame>
        )
    }

    if (schedule.programId === null) {
        return (
            <ScreenFrame>
                <div className='max-w-2xl mx-auto space-y-4'>
                    <KinesisProfileBreadCrumbs 
                        propProfileType={propProfileType} 
                        profileName={location.state?.profileName} 
                        currentPageName='Schedule' 
                    />

                    <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
                        Schedule
                    </h1>

                    <ContentFrame>
                        <div className="p-10 space-y-4 text-center">
                            <div className="space-y-2">
                                <p className="text-gray-600">No program active</p>
                                <p className="text-sm text-gray-500">Start a program by adding an A race event on the profile screen</p>
                            </div>

                            <div className="w-40 mx-auto">
                                <BrandColorButton
                                    size='extra-small'
                                    text="Go to Profile"
                                    action={OnGoToProfileButton}
                                />
                            </div>
                        </div>
                    </ContentFrame>
                </div>
            </ScreenFrame>
        )
    }

    const assignableWorkoutConfigs = GetAssignableWorkoutConfigs()
    const primaryColorHex = GetPrimaryColorHex(styleConfig)

    return (
        <ScreenFrame>
            <div className='max-w-2xl mx-auto space-y-4'>
                <KinesisProfileBreadCrumbs 
                    propProfileType={propProfileType} 
                    profileName={location.state?.profileName} 
                    currentPageName='Schedule' 
                />

                <div className='flex items-center gap-x-4 pb-1'>
                    <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex-grow">
                        Schedule
                    </h1>

                    <div className="w-32">
                        <BrandColorButton
                            size='extra-small'
                            text="Save"
                            action={OnSaveChangesButton}
                            isLoading={isSaveChangesLoading}
                        />
                    </div>
                </div>

                {formErrorMessage !== '' && <p className="text-center text-sm text-red-600">{formErrorMessage}</p>}

                <ContentFrame>
                    <div className="w-full p-6 space-y-4">

                        {/* Workouts left to schedule this week */}

                        <div className="flex flex-wrap justify-center gap-2">
                            {workoutConfigs.map((workoutConfig) => {
                                const assignmentCount = GetAssignmentCountForConfig(workoutConfig.workoutConfigId)
                                const maximumAssignmentsPerWeek = workoutConfig.maximumAssignmentsPerWeek ?? 0
                                const isAtMaximum = assignmentCount >= maximumAssignmentsPerWeek

                                return (
                                    <div
                                        key={workoutConfig.workoutConfigId}
                                        className="inline-flex items-center gap-x-1.5 rounded-xl bg-white border border-gray-200 px-3 py-1.5"
                                    >
                                        <p className="text-xs font-medium text-gray-800">{workoutConfig.name}</p>
                                        <p
                                            className={`text-xs tabular-nums ${isAtMaximum ? 'font-semibold' : 'text-gray-500'}`}
                                            style={{ color: isAtMaximum ? primaryColorHex : undefined }}
                                        >
                                            {assignmentCount}/{maximumAssignmentsPerWeek}
                                        </p>
                                    </div>
                                )
                            })}
                        </div>

                        {/* Week at a glance */}

                        <div className="flex justify-center gap-x-1.5">
                            {WEEKDAYS.map((weekday) => {
                                const count = GetAssignmentsForDay(weekday.ISOWeekdayIndex).length

                                return (
                                    <div key={weekday.ISOWeekdayIndex} className="flex w-9 flex-col items-center gap-y-1">
                                        <p className="text-xxs font-medium text-gray-500">{weekday.label.slice(0, 1)}</p>
                                        <div
                                            className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${count > 0 ? 'text-white' : 'bg-gray-100 text-gray-400'}`}
                                            style={{ backgroundColor: count > 0 ? primaryColorHex : undefined }}
                                        >
                                            {count}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>

                        {/* Day by day schedule */}

                        <div className="rounded-2xl border border-gray-200 overflow-hidden">
                            <div className="w-full divide-y divide-gray-200">
                                {WEEKDAYS.map((weekday) => {
                                    const dayAssignments = GetAssignmentsForDay(weekday.ISOWeekdayIndex)

                                    return (
                                        <div key={weekday.ISOWeekdayIndex} className="flex items-center gap-x-3 px-4 py-3">
                                            <p className="w-24 shrink-0 text-sm font-semibold text-left text-gray-900">{weekday.label}</p>

                                            <div className="flex min-h-7 min-w-0 flex-1 flex-wrap items-center gap-2">
                                                {dayAssignments.length === 0 && (
                                                    <p className="text-xs text-gray-400">Rest day</p>
                                                )}

                                                {dayAssignments.map((assignment, index) => (
                                                    <div
                                                        key={assignment.workoutConfigWeekdayAssignmentId ?? `${assignment.workoutConfigId}-${index}`}
                                                        className="inline-flex items-center gap-x-1.5 rounded-lg bg-gray-100 pl-2.5 pr-1 py-1"
                                                    >
                                                        <p className="text-xs text-gray-800">
                                                            {workoutConfigsById.get(assignment.workoutConfigId)?.name ?? 'Unknown workout'}
                                                        </p>

                                                        <button
                                                            type="button"
                                                            className="rounded p-0.5 text-gray-400 hover:text-gray-600"
                                                            onClick={() => RemoveAssignment(assignment)}
                                                        >
                                                            <span className="sr-only">Remove</span>
                                                            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                                                        </button>
                                                    </div>
                                                ))}

                                                <div className="flex-1" />

                                                {assignableWorkoutConfigs.length > 0 && (
                                                    <div className="relative h-7 w-[64px] shrink-0">
                                                        <PlusIcon
                                                            className="pointer-events-none absolute left-1.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
                                                            aria-hidden="true"
                                                        />

                                                        <select
                                                            className="h-7 w-full appearance-none rounded-lg border border-dashed border-gray-300 bg-white pl-6 pr-1 py-0 text-xs text-gray-600 cursor-pointer"
                                                            value=""
                                                            onChange={(event) => AddAssignment(weekday.ISOWeekdayIndex, event.target.value)}
                                                        >
                                                            <option value="" disabled>Add</option>

                                                            {assignableWorkoutConfigs.map((workoutConfig) => (
                                                                <option key={workoutConfig.workoutConfigId} value={workoutConfig.workoutConfigId}>
                                                                    {workoutConfig.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>

                    </div>
                </ContentFrame>
            </div>
        </ScreenFrame>
    )
}
